/**
 * Cinematic post pipeline and shadowing.
 *
 * These are the structural reasons a procedural rig reads as plastic, and no amount of
 * material tuning substitutes for them:
 *
 *  - **Shadows.** Without them every face receives the same light and the structure has
 *    no depth. Deep shade under decks and between members is most of what makes a real
 *    platform look heavy.
 *  - **Ambient occlusion.** Contact darkening in corners and crevices is what stops
 *    parts looking pasted together rather than joined.
 *  - **Tone mapping, bloom, vignette, grain.** The reference is a graded photograph. A
 *    raw linear buffer never looks like one.
 */

import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { SSAO2RenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import type { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Environment } from './environment';
import type { QualityTier } from './world';

import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Rendering/geometryBufferRendererSceneComponent';
import '@babylonjs/core/Rendering/prePassRendererSceneComponent';

export interface PostChain {
  pipeline: DefaultRenderingPipeline;
  ssao: SSAO2RenderingPipeline | null;
  shadows: ShadowGenerator | null;
  setEnvironment(env: Environment): void;
  dispose(): void;
}

export function createPostChain(
  scene: Scene,
  camera: Camera,
  sun: DirectionalLight,
  shadowCasters: Mesh[],
  quality: QualityTier,
): PostChain {
  const highEnd = quality === 'ultra' || quality === 'high';

  // --- Shadows ---------------------------------------------------------------------
  let shadows: ShadowGenerator | null = null;
  if (quality !== 'low') {
    const size = quality === 'ultra' ? 2048 : quality === 'high' ? 2048 : 1024;
    shadows = new ShadowGenerator(size, sun);
    // Contact-hardening PCF reads far better than plain PCF on lattice work, where
    // thin members would otherwise produce shimmering aliased shadow edges.
    shadows.usePercentageCloserFiltering = true;
    shadows.filteringQuality = highEnd
      ? ShadowGenerator.QUALITY_HIGH
      : ShadowGenerator.QUALITY_MEDIUM;
    shadows.bias = 0.0008;
    shadows.normalBias = 0.02;
    shadows.darkness = 0.28;
    shadows.transparencyShadow = true;

    const map = shadows.getShadowMap();
    if (map) map.renderList = shadowCasters.slice();
    for (const m of shadowCasters) m.receiveShadows = true;

    // The rig is ~60m across and ~200m tall; the shadow frustum must cover it.
    sun.autoUpdateExtends = false;
    sun.autoCalcShadowZBounds = false;
    sun.shadowMinZ = -220;
    sun.shadowMaxZ = 360;
    sun.orthoLeft = -70;
    sun.orthoRight = 70;
    sun.orthoBottom = -130;
    sun.orthoTop = 130;
  }

  // --- Ambient occlusion -------------------------------------------------------------
  let ssao: SSAO2RenderingPipeline | null = null;
  if (highEnd) {
    ssao = new SSAO2RenderingPipeline('ssao', scene, { ssaoRatio: 0.6, blurRatio: 1 }, [camera]);
    ssao.radius = 0.9;
    ssao.totalStrength = 0.55;
    ssao.expensiveBlur = true;
    ssao.samples = 12;
    ssao.maxZ = 120;
    ssao.minZAspect = 0.25;
  }

  // --- Grade -------------------------------------------------------------------------
  const pipeline = new DefaultRenderingPipeline('deepwaterPost', true, scene, [camera]);

  pipeline.imageProcessingEnabled = true;
  const ip = pipeline.imageProcessing;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  ip.exposure = 1;
  ip.contrast = 1.05;
  ip.vignetteEnabled = true;
  ip.vignetteWeight = 1.2;
  ip.vignetteStretch = 0.9;
  ip.vignetteCameraFov = 1.05;

  // Bloom only on genuinely bright things — foam crests, floodlights, muzzle flash later.
  pipeline.bloomEnabled = true;
  /*
   * Bloom carries the lamps now, not their emissive values.
   *
   * A hot source with no glow reads as a white sticker; a modest source with a real halo
   * reads as a light. So the faces came down and this came up — the threshold drops below
   * 1.0 so a lamp that is deliberately NOT clipping still blooms, and the weight roughly
   * doubles so the halo is the dominant part of the effect rather than a rim.
   */
  pipeline.bloomThreshold = 0.85;
  pipeline.bloomWeight = 0.38;
  pipeline.bloomKernel = 32;
  pipeline.bloomScale = 0.5;

  // A little grain keeps flat overcast sky from banding and adds photographic texture.
  pipeline.grainEnabled = true;
  pipeline.grain.intensity = 4.0;
  pipeline.grain.animated = true;

  pipeline.sharpenEnabled = highEnd;
  if (highEnd) {
    pipeline.sharpen.edgeAmount = 0.22;
    pipeline.sharpen.colorAmount = 1;
  }

  // FXAA rather than MSAA: once the frame goes through a post chain, MSAA no longer
  // applies to the composited result anyway.
  pipeline.fxaaEnabled = true;
  pipeline.samples = 1;

  return {
    pipeline,
    ssao,
    shadows,
    setEnvironment(env: Environment): void {
      pipeline.imageProcessing.exposure = env.exposure;
      // Heavier vignette and softer bloom as conditions darken.
      pipeline.imageProcessing.vignetteWeight = 1.0 + (1 - env.ambientIntensity) * 0.8;
    },
    dispose(): void {
      pipeline.dispose();
      ssao?.dispose();
      shadows?.dispose();
    },
  };
}
